import { useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';

import { Panel, Section } from '../../../components/Panel.tsx';
import { DataTable, type Column } from '../../../components/DataTable.tsx';
import { Empty, ErrorLine } from '../../../components/Empty.tsx';
import { Timestamp } from '../../../components/Timestamp.tsx';
import { StateLabel } from '../../../components/StatusBadge.tsx';
import { useRead } from '../../../components/states/read.ts';
import {
  NotReadyError,
  usePlayback,
  useRecordings,
  type Recording,
} from '../../../hooks/useRecordings.ts';
import styles from './VideosTab.module.css';

/* -------------------------------------------------------------------------
   Videos — flagged session recordings (PRD-0010 D11, #248).

   THIS TAB SITS NEXT TO CAPTURES, WHICH IS THE WHOLE PROBLEM IT HAS TO SOLVE.

   Two tabs side by side, both listing sessions from the same robot on the same
   afternoon, invite exactly one assumption: that they are two views of one
   thing. They are not.

     Captures   the robot's own MCAP record. Written to local disk as it
                happens, complete regardless of the network, and AUTHORITATIVE
                (PRD-0002 D6).

     Videos     the cloud-side composition of what an operator was watching.
                It is assembled from frames that survived the field link, so it
                goes lossy or frozen exactly when that link degrades — during
                the incident somebody is most likely to review it for.

   So the distinction is stated where it is read, in the tab itself, and again
   on the player at the moment somebody is about to treat a recording as the
   record of an event. Not in a doc nobody opens.

   Retention is shown for the same reason: every row is on a 30-day clock, and a
   clock nobody can see is indistinguishable from footage going missing.
   ------------------------------------------------------------------------- */

const DAY_MS = 24 * 3600 * 1000;

function durationLabel(seconds: number | null): string {
  if (seconds === null) return '—';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

/** Days left on the 30-day clock. Floored, so it never over-promises. */
function daysLeft(expiresAt: string, now: number): number {
  return Math.floor((new Date(expiresAt).getTime() - now) / DAY_MS);
}

function shortCamera(topic: string): string {
  if (!topic) return 'default';
  const parts = topic.split('/').filter(Boolean);
  return parts.length <= 1 ? topic : `${parts[0]}/${parts[parts.length - 2] ?? ''}`;
}

export function VideosTab({ robotId }: { robotId: string }) {
  const query = useRecordings(robotId);
  const { data, error } = query;
  /* The same read discipline as every other table here: a source that did not
     answer must not render as a source that answered with nothing. */
  const read = useRead(query, {
    path: `/api/robots/${robotId}/video/recordings`,
    everyMs: 30_000,
    subject: 'the recordings',
  });
  const [open, setOpen] = useState<string | null>(null);
  const now = Date.now();

  const recordings = useMemo(() => data?.recordings ?? [], [data]);
  const retention = data?.retention_days ?? 30;

  /* Selecting a row that has aged out between renders would open a player on
     objects S3 has deleted. The list already filters those out server-side; this
     keeps the local selection honest if one expires while the tab is open. */
  const selected = useMemo(
    () => recordings.find((r) => r.session_id === open) ?? null,
    [recordings, open],
  );

  const columns: readonly Column<Recording>[] = [
    {
      key: 'started', header: 'Recorded', kind: 'age', align: 'right',
      cell: (r) => <Timestamp value={r.started_at} strong />,
    },
    {
      key: 'dur', header: 'Length', kind: 'age', align: 'right',
      cell: (r) => durationLabel(r.duration_s),
    },
    {
      key: 'by', header: 'Flagged by', kind: 'identity', max: '22ch',
      cell: (r) => <span title={r.started_by}>{r.started_by}</span>,
    },
    {
      key: 'cam', header: 'Camera', kind: 'identity', mono: true, max: '18ch', drop: 1,
      cell: (r) => (
        <span title={r.camera_topic || 'robot default'}>
          {shortCamera(r.camera_topic)}
        </span>
      ),
    },
    {
      key: 'state', header: 'State', kind: 'status',
      cell: (r) => (r.playable
        ? <StateLabel tone="ok" label="Ready" />
        : <StateLabel tone="live" label="Recording" />),
    },
    {
      key: 'expires', header: 'Deleted in', kind: 'age', align: 'right', drop: 2,
      cell: (r) => {
        const d = daysLeft(r.expires_at, now);
        return (
          <span className={d <= 3 ? styles.expiringSoon : undefined}>
            {d <= 0 ? 'today' : `${d}d`}
          </span>
        );
      },
    },
  ];

  if (data !== undefined && !data.recording_available) {
    return (
      <Panel title="Videos">
        <Empty>
          Session recording is not configured in this environment, so no
          recordings can be made or listed here.
        </Empty>
      </Panel>
    );
  }

  return (
    <div className={styles.wrap}>
      <Panel title="Videos">
        {/* Said once, at the top, in the words that matter — and repeated on
            the player. */}
        <div className={styles.notAuthoritative} role="note">
          <strong>These are not the field record.</strong> A video here is the
          cloud-side copy of what an operator was watching, so it drops frames
          exactly when the robot&apos;s link does. The complete record of a
          session is the robot&apos;s own capture, under{' '}
          <em>Captures</em>. Deleted automatically after {retention} days.
        </div>

        {error !== null && error !== undefined
          ? <ErrorLine>Could not load recordings: {String(error)}</ErrorLine>
          : (
            <DataTable
              rows={recordings}
              columns={columns}
              rowKey={(r) => r.session_id}
              onRowClick={(r) => setOpen(r.playable ? r.session_id : null)}
              currentKey={open ?? undefined}
              of={read}
              empty="No sessions from this robot have been flagged for recording."
            />
          )}
      </Panel>

      {selected !== null && (
        <Panel title="Playback">
          <RecordingPlayer
            robotId={robotId}
            recording={selected}
            onClose={() => setOpen(null)}
          />
        </Panel>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------
   The player.

   The API hands back an HLS playlist whose segment URIs are ALREADY presigned,
   rather than a URL — see pipeline/video_recordings.py for why signing "the
   recording" cannot work when a recording is a playlist plus hundreds of
   private objects. So the playlist is turned into a blob URL and handed to
   hls.js, which then fetches segments straight from S3.

   Native HLS (Safari) is preferred where it exists, because it decodes in
   hardware; hls.js covers everything else. Chrome cannot play HLS without it,
   and Chrome is what operators use.
   ------------------------------------------------------------------------- */

function RecordingPlayer({
  robotId, recording, onClose,
}: {
  robotId: string;
  recording: Recording;
  onClose: () => void;
}) {
  const { data, error, isLoading } = usePlayback(robotId, recording.session_id);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playerError, setPlayerError] = useState<string | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video === null || data === undefined) return;

    setPlayerError(null);
    const blob = new Blob([data.playlist], {
      type: 'application/vnd.apple.mpegurl',
    });
    const url = URL.createObjectURL(blob);

    let hls: Hls | null = null;
    if (video.canPlayType('application/vnd.apple.mpegurl') !== '') {
      video.src = url;
    } else if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true });
      hls.on(Hls.Events.ERROR, (_evt, detail) => {
        /* Only fatal errors reach the operator. hls.js reports recoverable
           ones constantly on a VOD playlist and surfacing those would make a
           working video look broken. */
        if (detail.fatal) {
          setPlayerError(
            detail.type === Hls.ErrorTypes.NETWORK_ERROR
              ? 'Could not fetch the video. Its links may have expired — reopen it.'
              : 'This recording could not be decoded.',
          );
        }
      });
      hls.loadSource(url);
      hls.attachMedia(video);
    } else {
      setPlayerError('This browser cannot play HLS video.');
    }

    return () => {
      hls?.destroy();
      /* Revoked on teardown, not on load: revoking while hls.js still holds the
         manifest aborts the very first fetch. */
      URL.revokeObjectURL(url);
    };
  }, [data]);

  if (isLoading) return <Empty>Preparing playback…</Empty>;

  if (error instanceof NotReadyError) {
    return (
      <Empty>
        This session is still recording. Composition is written as the session
        runs and only finalises when it ends — playing it now would stop at
        whatever had been written when you pressed play.
      </Empty>
    );
  }
  if (error !== null && error !== undefined) {
    return <ErrorLine>Could not prepare playback: {String(error)}</ErrorLine>;
  }

  return (
    <>
      <Section
        label={`${recording.session_id.slice(0, 12)} · ${durationLabel(recording.duration_s)}`}
        aside={<button type="button" className={styles.close} onClick={onClose}>Close</button>}
      >
        {playerError !== null && <ErrorLine>{playerError}</ErrorLine>}
        <video
          ref={videoRef}
          className={styles.video}
          controls
          playsInline
          preload="metadata"
        />
        <div className={styles.playerNote}>
          Cloud-side copy — frames lost to the link are missing here and are not
          recoverable from this file. {data?.width !== null && data?.height !== null
            && `${data?.width}×${data?.height}. `}
          For the complete record of these minutes, use the robot&apos;s capture
          under <em>Captures</em>.
        </div>
      </Section>
    </>
  );
}
